import { t } from "@/lib/i18n/interpolate";
import { apaInTextAuthors } from "@/lib/tool-kit/citation-parts";
import {
  asClause,
  asSentence,
  capitalizeFirst,
  fillFrame,
  properWords,
  stripEnd,
  tidy,
} from "@/lib/tool-kit/essay/sentence";
import {
  buildSignalPhrases,
  formatLocator,
  laterMentionNames,
  parseAuthors,
  type SignalPhrase,
  type SignalStance,
  type SignalStyle,
  type SignalWords,
} from "@/lib/tool-kit/essay/signal-phrase";
import { countWords } from "@/lib/tool-kit/essay-templates/text";

/**
 * The quote sandwich: introduce a quotation, cite it, explain it.
 *
 * Teachers also call it ICE (Introduce–Cite–Explain) or a quotation sandwich: the
 * quotation is the filling, a lead-in is the top slice, and the explanation is the
 * bottom slice. The introduction and citation follow the style's in-text rules (MLA 9:
 * page in parentheses with no "p."; APA 7: author, year, and "p." for a direct
 * quotation) through the same signal-phrase builder the Signal Phrase Generator uses.
 * The explanation is assembled from fixed sentence frames and the student's own words:
 * what the quotation shows, which words carry it, why it matters, and the claim it
 * supports. Nothing about the quotation is inferred.
 */

export type SandwichStyle = Extract<SignalStyle, "mla" | "apa">;
export type SandwichMode = "author" | "character";

export const SANDWICH_STYLES: readonly SandwichStyle[] = ["mla", "apa"];
export const SANDWICH_MODES: readonly SandwichMode[] = ["author", "character"];

export interface SandwichInput {
  style: SandwichStyle;
  mode: SandwichMode;
  stance: SignalStance;
  /** One author per line. */
  authors: string;
  year: string;
  page: string;
  quote: string;
  /** Who is speaking, in character mode: "Hamlet", "the narrator". */
  speaker: string;
  /** What the speaker does, in character mode, present tense: "warns". */
  verb: string;
  /** An optional sentence that sets the scene before the quotation is introduced. */
  leadIn: string;
  claim: string;
  meaning: string;
  keyword: string;
  suggests: string;
  why: string;
}

export interface SandwichWords {
  signal: SignalWords;
  /** Frames for a character speaking: `{who} {verb}, {quote} {cite}.` */
  characterFrames: readonly string[];
  /** The explanation frames, in order. Empty fields drop their sentence. */
  explainFrames: readonly string[];
}

export const CHARACTER_FRAMES = [
  "{who} {verb}, {quote} {cite}.",
  "As {who} {verb}, {quote} {cite}.",
] as const;

export const EXPLAIN_FRAMES = [
  "This shows that {meaning}.",
  "{keyword} suggests {suggests}.",
  "This matters because {why}.",
  "In this way, the quotation supports the claim that {claim}.",
] as const;

/** The verbs offered for a character's words, present tense (literary analysis). */
export const CHARACTER_VERBS = [
  "says",
  "admits",
  "insists",
  "declares",
  "warns",
  "realizes",
  "wonders",
  "replies",
] as const;

export interface Sandwich {
  /** The signal-phrase pattern, or "character" with the verb. */
  id: string;
  /** The top slice: lead-in and the words before the quotation. */
  introduce: string;
  /** The filling and the citation: the quotation with its parenthetical. */
  quote: string;
  /** The bottom slice. */
  explain: string;
  /** All three as a paragraph, ready to paste. */
  full: string;
}

const RE_OUTER_QUOTES = /^["“‘']+|["”’']+$/g;
const RE_TRAILING_SOFT = /[\s.,;:]+$/;
const RE_STARTS_WITH_DIGIT = /^\d/;
const QUOTE_SLOT = "{quote}";
const CITE_SLOT = "{cite}";
const RE_QUOTE_PART = /^([^“]*)(“[\s\S]*)$/;

/** The quotation's own words: outer marks and a closing comma or period removed. */
export function cleanQuote(text: string): string {
  return tidy(text).replace(RE_OUTER_QUOTES, "").replace(RE_TRAILING_SOFT, "");
}

/** The words inside the quotation marks, for the explanation's key-word sentence. */
function cleanKeyword(text: string): string {
  return stripEnd(tidy(text).replace(RE_OUTER_QUOTES, ""));
}

/** MLA: "(Shelley 5)". APA: "(Shelley, 1818, p. 5)". */
export function characterCitation(
  input: SandwichInput,
  words: SignalWords
): string {
  const names = parseAuthors(input.authors, false);
  const locator =
    tidy(input.page) === "" ? "" : formatLocator(input.page, input.style);
  if (input.style === "mla") {
    const surname =
      names.length > 0 ? laterMentionNames(names, "mla") : words.blank;
    if (locator === "") {
      return `(${surname})`;
    }
    // A page follows the surname with a space; a chapter or section after a comma.
    const joiner = RE_STARTS_WITH_DIGIT.test(locator) ? " " : ", ";
    return `(${surname}${joiner}${locator})`;
  }
  const authors =
    names.length > 0 ? apaInTextAuthors(names, false) : words.blank;
  const year = tidy(input.year) === "" ? words.noDate : tidy(input.year);
  return `(${authors}, ${year}, ${locator === "" ? words.pageBlank : locator})`;
}

/** The words that remain once the quotation and citation are taken out. */
function splitPhrase(
  text: string
): { before: string; quotation: string } | null {
  const match = RE_QUOTE_PART.exec(text);
  return match ? { before: tidy(match[1]), quotation: match[2] } : null;
}

/** The explanation sentences from the student's own words; empty fields drop out. */
export function buildExplanation(
  input: SandwichInput,
  frames: readonly string[] = EXPLAIN_FRAMES
): string {
  const proper = properWords([
    input.meaning,
    input.suggests,
    input.why,
    input.claim,
  ]);
  const clause = (text: string) =>
    tidy(text) === "" ? null : asClause(text, proper);
  const keyword = cleanKeyword(input.keyword);
  const values: Record<string, string | null> = {
    meaning: clause(input.meaning),
    keyword: keyword === "" ? null : `“${keyword}”`,
    suggests: clause(input.suggests),
    why: clause(input.why),
    claim: clause(input.claim),
  };
  return frames
    .map((frame) => fillFrame(frame, values))
    .filter((sentence): sentence is string => sentence !== null)
    .join(" ");
}

/** The words the student wrote for the explanation — what the length check counts. */
export function explanationWords(input: SandwichInput): number {
  return countWords(
    [input.meaning, input.suggests, input.why].map(tidy).join(" ")
  );
}

export function quoteWords(input: SandwichInput): number {
  return countWords(cleanQuote(input.quote));
}

export type LengthAdvice = "ok" | "trim" | "block";

/** APA sets a quotation of 40 words or more as a block; MLA at more than four lines. */
export const BLOCK_WORDS: Readonly<Record<SandwichStyle, number>> = {
  apa: 40,
  // About four typed lines of prose in 12-point type with one-inch margins.
  mla: 50,
};

/** A soft limit past which a quotation is harder to explain than to quote. */
export const TRIM_WORDS = 30;

export function lengthAdvice(input: SandwichInput): LengthAdvice {
  const words = quoteWords(input);
  const block = BLOCK_WORDS[input.style];
  if (words >= block) {
    return "block";
  }
  return words >= TRIM_WORDS ? "trim" : "ok";
}

function assemble(
  id: string,
  introduce: string,
  quote: string,
  explain: string,
  leadIn: string
): Sandwich {
  const parts = [leadIn, `${introduce} ${quote}`.trim(), explain].filter(
    (part) => part !== ""
  );
  const full = parts.join(" ");
  return { id, introduce, quote, explain, full };
}

function authorSandwiches(
  input: SandwichInput,
  words: SandwichWords,
  explain: string,
  leadIn: string
): Sandwich[] {
  const phrases: SignalPhrase[] = buildSignalPhrases(
    {
      authors: input.authors,
      organization: false,
      year: input.year,
      page: input.page,
      text: cleanQuote(input.quote),
    },
    {
      style: input.style,
      stance: input.stance,
      use: "quote",
      apaTense: "past",
    },
    words.signal
  ).filter((phrase) => phrase.pattern !== "interrupt");
  return phrases.map((phrase) => {
    const split = splitPhrase(phrase.text);
    return assemble(
      phrase.pattern,
      split?.before ?? "",
      split?.quotation ?? phrase.text,
      explain,
      leadIn
    );
  });
}

function characterSandwiches(
  input: SandwichInput,
  words: SandwichWords,
  explain: string,
  leadIn: string
): Sandwich[] {
  const who =
    tidy(input.speaker) === "" ? words.signal.blank : stripEnd(input.speaker);
  const body = cleanQuote(input.quote);
  const quotation = `${words.signal.openQuote}${body}${words.signal.closeQuote}`;
  const cite = characterCitation(input, words.signal);
  return words.characterFrames.map((frame, index) => {
    const verb =
      tidy(input.verb) === "" ? words.signal.blank : tidy(input.verb);
    const quoteAt = frame.indexOf(QUOTE_SLOT);
    const citeAt = frame.indexOf(CITE_SLOT);
    const before = t(frame.slice(0, quoteAt), { who, verb }).trim();
    const closing = frame.slice(citeAt + CITE_SLOT.length).trim();
    return assemble(
      `character-${index + 1}`,
      capitalizeFirst(before),
      `${quotation} ${cite}${closing}`,
      explain,
      leadIn
    );
  });
}

/** Every sandwich the style, mode, and stance allow. Empty until a quotation is entered. */
export function buildSandwiches(
  input: SandwichInput,
  words: SandwichWords
): Sandwich[] {
  if (cleanQuote(input.quote) === "") {
    return [];
  }
  const explain = buildExplanation(input, words.explainFrames);
  const leadIn = tidy(input.leadIn) === "" ? "" : asSentence(input.leadIn);
  return input.mode === "author"
    ? authorSandwiches(input, words, explain, leadIn)
    : characterSandwiches(input, words, explain, leadIn);
}
